import type { Product } from 'types/models/product'
import type { Variables } from '~/contracts/ProductRepositoryContract'
import type { FilterGroup } from '~/types/models/filter.model'
import { isProduct } from 'types/models/product'
import { ITEMS_PER_OUTLET_PAGE, ITEMS_PER_PAGE } from '~/configuration/global.configuration'
import { getTenant } from '~/helpers/tenant'
import { assignFacetVariables } from '~/utils/products/assignFacetVariables'

interface FetchCategoryProps {
  category: string
  page?: number
  widgetId?: string
  facets?: FilterGroup[]
  filters?: FilterGroup[]
}
export interface ProductRequestResponse {
  products: Product[]
  pages: number
  id: string
  total: number
  redirects?: string[]
  facets?: FilterGroup[]
}

type ProductsMap = Record<string, Product>

export const useProductStore = defineStore('ProductStore', () => {
  const products = ref<ProductsMap>({})
  const fetchingProductForPDP = ref(false)
  const fetchingProductsForCategory = ref(false)
  const recommendations = ref<string[]>([])
  const cluster = ref<string[]>([])
  const similar = ref<string[]>([])
  const currentProduct = ref<Product>()
  const currentWidgetId = ref<string | undefined>()
  const widgetPdp = ref<string | undefined>()

  const rootStore = useRootStore()

  const relatedProductsByColor = computed(() => {
    return cluster.value.map(sku => products.value[sku]!)
  })

  const currentProductAndCluster = computed(() => {
    if (!cluster.value || !currentProduct.value)
      return undefined

    return [currentProduct.value, ...relatedProductsByColor.value]
  })

  const getProductBySku = (sku: string) => {
    const product = products.value[sku]

    if (!product)
      throw new Error(`Product not found: ${sku}`)

    return product
  }

  const setProducts = (listOfProducts: Product[]) => {
    listOfProducts.forEach((product) => {
      products.value[product.sku] = product
    })
  }

  function setWidget() {
    const storefrontStore = useStorefrontStore()
    const { XOEnvironment } = useRuntimeConfig().public
    const tenant = getTenant(storefrontStore.currentStorefrontCode, XOEnvironment, {
      tenantKey: storefrontStore.current.tenantKey,
    })

    widgetPdp.value = tenant.widgets.pdp
  }

  function setProductAndAssociations(product: Product, args: {
    recommendations: string[]
    cluster: string[]
    similar: string[]
    id?: string
  }) {
    currentProduct.value = product
    recommendations.value = args.recommendations
    cluster.value = args.cluster
    similar.value = args.similar

    if (args.id)
      rootStore.addEarlyBirdRecord(args.id)

    if (widgetPdp.value)
      currentWidgetId.value = widgetPdp.value
  }

  const fetchProductForPDP = async (sku: string) => {
    try {
      currentProduct.value = undefined
      fetchingProductForPDP.value = true

      const { $products } = useNuxtApp()
      const {
        id,
        products,
        recommendations: fetchedRecommendations,
        cluster: fetchedCluster,
        similar: fetchedSimilar,
      } = await $products.fetchProductForPDP(sku)

      setProducts(products)

      setProductAndAssociations(products[0], {
        recommendations: fetchedRecommendations,
        cluster: fetchedCluster,
        similar: fetchedSimilar,
        id,
      })

      return Promise.resolve({
        id,
        product: products[0],
        recommendations: fetchedRecommendations,
        cluster: fetchedCluster,
        similar: fetchedSimilar,
      })
    }
    catch (e) {
      return Promise.reject(e)
    }
    finally {
      fetchingProductForPDP.value = false
    }
  }

  const fetchProductsForCategory = async ({
    category,
    page,
    widgetId,
    facets,
    filters,
  }: FetchCategoryProps): Promise<ProductRequestResponse> => {
    try {
      const { $products } = useNuxtApp()
      fetchingProductsForCategory.value = true

      const facetVariables = facets ? assignFacetVariables(facets) : {}

      const getNumberOfItemsPerPage = () => {
        if (category.startsWith('category-outlet-all'))
          return ITEMS_PER_OUTLET_PAGE

        return ITEMS_PER_PAGE
      }

      const { id, items, pages, totalItems } = await $products.fetchProducts({
        page,
        widgetId,
        variables: {
          $collection: category,
          ...facetVariables,
        },
        limit: getNumberOfItemsPerPage(),
        filters,
      })

      const products = items.flat().filter(isProduct)

      rootStore.addEarlyBirdRecord(id)

      return Promise.resolve({ products, pages, id, total: totalItems })
    }
    catch (e) {
      return Promise.reject(e)
    }
    finally {
      fetchingProductsForCategory.value = false
    }
  }

  async function fetchProductsForBasket(options: { widgetId?: string, variables?: Variables }) {
    try {
      const cart = useCartStore()
      const storefrontStore = useStorefrontStore()

      const cartAmount = cart.state.priceTotal
      const itemSkus = cart.state.items.map(item => item.parentSku)
      const shippingThreshold = storefrontStore.freeShippingThreshold

      const { $products } = useNuxtApp()
      fetchingProductsForCategory.value = true

      const { id, items, pages, totalItems } = await $products.fetchBasketProducts({
        cartAmount,
        itemSkus,
        shippingThreshold,
        widgetId: options.widgetId,
        variables: options.variables,
      })

      const products = items.flat().filter(isProduct)

      rootStore.addEarlyBirdRecord(id)

      return Promise.resolve({ products, pages, id, total: totalItems })
    }
    catch (e) {
      return Promise.reject(e)
    }
    finally {
      fetchingProductsForCategory.value = false
    }
  }

  return {
    products,
    recommendations,
    cluster,
    similar,

    relatedProductsByColor,
    currentProductAndCluster,
    currentProduct,

    setProductAndAssociations,
    setWidget,

    getProductBySku,
    setProducts,
    currentWidgetId,
    fetchProductForPDP,
    fetchingProductForPDP,
    fetchProductsForCategory,
    fetchingProductsForCategory,
    fetchProductsForBasket,
  }
})
